import React, { useState, useRef } from 'react';
import {
  Box, Button, VStack, HStack, Text, Alert, AlertIcon,
  Input, Progress, Badge, useToast, Icon, Divider,
  Table, Thead, Tbody, Tr, Th, Td, useColorModeValue,
  Stat, StatLabel, StatNumber, StatGroup, Tooltip,
} from '@chakra-ui/react';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import { FiUpload, FiFile, FiCheck, FiX, FiAlertCircle, FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { importBecados } from 'services/woo';

// Normaliza un RUT quitando puntos y dejando el guión
const normalizeRut = (raw) => {
  if (!raw) return '';
  return String(raw).trim().replace(/\./g, '').replace(/\s/g, '');
};

// Detecta cuál columna contiene los RUTs buscando por nombre de cabecera
const detectRutColumn = (headers) => {
  const patterns = ['rut', 'username', 'user', 'run', 'rut_alumno', 'rut alumno'];
  const lower = headers.map((h) => String(h ?? '').toLowerCase().trim());
  const idx = lower.findIndex((h) => patterns.some((p) => h.includes(p)));
  return idx >= 0 ? idx : 0; // fallback a primera columna
};

const detectColumn = (headers, patterns) => {
  const lower = headers.map((h) => String(h ?? '').toLowerCase().trim());
  return lower.findIndex((h) => patterns.some((p) => h.includes(p)));
};

const BecadosImporter = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, rutCol, nombreCol, cursoCol }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);
  const toast = useToast();

  const bgCard   = useColorModeValue('white', 'gray.800');
  const border   = useColorModeValue('gray.200', 'gray.600');
  const dropBg   = useColorModeValue('gray.50', 'gray.700');
  const dropActiveBg = useColorModeValue('purple.50', 'purple.900');
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  const parseExcel = (selectedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (!data || data.length < 2) {
          toast({ title: 'Archivo vacío o sin datos', status: 'error', duration: 4000, isClosable: true });
          return;
        }

        const headers = data[0].map(String);
        const rows    = data.slice(1).filter((r) => r.some((c) => c !== ''));

        const rutCol    = detectRutColumn(headers);
        const nombreCol = detectColumn(headers, ['nombre', 'name', 'alumno', 'apellido']);
        const cursoCol  = detectColumn(headers, ['curso', 'grado', 'grupo', 'clase', 'level']);

        setPreview({ headers, rows, rutCol, nombreCol, cursoCol });
        setResult(null);
      } catch (err) {
        toast({ title: 'Error al leer el archivo', description: err.message, status: 'error', duration: 5000, isClosable: true });
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleFileSelect = (selected) => {
    if (!selected) return;
    const ext = selected.name.toLowerCase().split('.').pop();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast({ title: 'Formato no válido', description: 'Solo se aceptan archivos .xlsx, .xls o .csv', status: 'error', duration: 4000, isClosable: true });
      return;
    }
    setFile(selected);
    setPreview(null);
    setResult(null);
    parseExcel(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  };

  const handleImport = async () => {
    if (!preview) return;
    const { rows, rutCol } = preview;
    const ruts = rows.map((r) => normalizeRut(r[rutCol])).filter(Boolean);

    if (ruts.length === 0) {
      toast({ title: 'No se encontraron RUTs en el archivo', status: 'warning', duration: 4000, isClosable: true });
      return;
    }

    setImporting(true);
    setProgress(10);

    try {
      setProgress(40);
      const data = await importBecados(ruts);
      setProgress(100);
      setResult(data);

      toast({
        title: `✅ ${data.updated_count} becados asignados`,
        description: data.not_found_count > 0 ? `${data.not_found_count} RUTs no encontrados en el sistema` : undefined,
        status: data.not_found_count > 0 ? 'warning' : 'success',
        duration: 6000,
        isClosable: true,
      });

      if (onImportComplete) onImportComplete(data);
    } catch (err) {
      toast({ title: 'Error al importar', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewRuts = preview ? preview.rows.slice(0, 8) : [];
  const totalRuts   = preview ? preview.rows.filter((r) => normalizeRut(r[preview.rutCol])).length : 0;

  return (
    <Card bg={bgCard} border="1px solid" borderColor={border} borderRadius="12px">
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold">Importar Becados desde Excel</Text>
            <Text fontSize="sm" color={mutedText}>
              Sube un Excel con RUT (username), nombre y curso. Solo se usa el RUT para asignar el rol.
            </Text>
          </VStack>
          {(file || result) && (
            <Button size="sm" variant="ghost" onClick={reset}>Limpiar</Button>
          )}
        </HStack>
      </CardHeader>

      <CardBody>
        <VStack spacing={4} align="stretch">

          {/* Zona de drop */}
          {!preview && !result && (
            <Box
              border="2px dashed"
              borderColor={dragActive ? 'purple.400' : border}
              borderRadius="10px"
              p={8}
              textAlign="center"
              bg={dragActive ? dropActiveBg : dropBg}
              cursor="pointer"
              transition="all 0.2s"
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon as={FiUpload} boxSize={8} color="purple.400" mb={3} />
              <Text fontWeight="medium">Arrastra tu Excel aquí o haz clic para seleccionar</Text>
              <Text fontSize="sm" color={mutedText} mt={1}>
                Formatos: .xlsx, .xls, .csv · Columnas: RUT, Nombre alumno, Curso
              </Text>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                display="none"
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
            </Box>
          )}

          {/* Archivo seleccionado */}
          {file && !result && (
            <HStack bg={dropBg} p={3} borderRadius="8px" spacing={3}>
              <Icon as={FiFile} color="purple.400" />
              <Text fontSize="sm" fontWeight="medium" flex={1}>{file.name}</Text>
              <Badge colorScheme="purple">{totalRuts} RUTs detectados</Badge>
            </HStack>
          )}

          {/* Preview de datos */}
          {preview && !result && (
            <>
              <Alert status="info" borderRadius="8px" py={2}>
                <AlertIcon />
                <Text fontSize="sm">
                  Se asignará el rol <strong>Becado</strong> a {totalRuts} usuarios encontrados por RUT.
                  Los que no existan en el sistema se reportarán como no encontrados.
                </Text>
              </Alert>

              <Box overflowX="auto" borderRadius="8px" border="1px solid" borderColor={border}>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>RUT</Th>
                      {preview.nombreCol >= 0 && <Th>Nombre</Th>}
                      {preview.cursoCol  >= 0 && <Th>Curso</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {previewRuts.map((row, i) => (
                      <Tr key={i}>
                        <Td fontFamily="mono" fontSize="xs">{normalizeRut(row[preview.rutCol])}</Td>
                        {preview.nombreCol >= 0 && <Td fontSize="xs">{String(row[preview.nombreCol] ?? '')}</Td>}
                        {preview.cursoCol  >= 0 && <Td fontSize="xs">{String(row[preview.cursoCol]  ?? '')}</Td>}
                      </Tr>
                    ))}
                    {totalRuts > 8 && (
                      <Tr>
                        <Td colSpan={3} textAlign="center" color={mutedText} fontSize="xs">
                          … y {totalRuts - 8} RUTs más
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {importing && (
                <Progress value={progress} colorScheme="purple" borderRadius="full" size="sm" hasStripe isAnimated />
              )}

              <HStack justify="flex-end">
                <Button
                  colorScheme="purple"
                  leftIcon={<Icon as={FiUpload} />}
                  onClick={handleImport}
                  isLoading={importing}
                  loadingText="Importando..."
                  isDisabled={totalRuts === 0}
                >
                  Importar {totalRuts} becados
                </Button>
              </HStack>
            </>
          )}

          {/* Resultados */}
          {result && (
            <VStack spacing={4} align="stretch">
              <StatGroup>
                <Stat>
                  <StatLabel>Asignados</StatLabel>
                  <StatNumber color="green.500">{result.updated_count}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>No encontrados</StatLabel>
                  <StatNumber color={result.not_found_count > 0 ? 'orange.400' : 'gray.400'}>
                    {result.not_found_count}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Errores</StatLabel>
                  <StatNumber color={result.error_count > 0 ? 'red.400' : 'gray.400'}>
                    {result.error_count}
                  </StatNumber>
                </Stat>
              </StatGroup>

              <Divider />

              {/* Asignados */}
              {result.results.updated.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2} color="green.500">
                    <Icon as={FiCheck} mr={1} />Rol Becado asignado ({result.results.updated.length})
                  </Text>
                  <Box overflowX="auto" borderRadius="8px" border="1px solid" borderColor={border}>
                    <Table size="sm">
                      <Thead><Tr><Th>RUT</Th><Th>Nombre</Th><Th>Rol anterior</Th></Tr></Thead>
                      <Tbody>
                        {result.results.updated.map((u, i) => (
                          <Tr key={i}>
                            <Td fontFamily="mono" fontSize="xs">{u.rut}</Td>
                            <Td fontSize="xs">{u.nombre}</Td>
                            <Td><Badge colorScheme="gray" fontSize="xs">{u.prev_role}</Badge></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}

              {/* No encontrados */}
              {result.results.not_found.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2} color="orange.400">
                    <Icon as={FiAlertCircle} mr={1} />RUTs no encontrados en el sistema ({result.results.not_found.length})
                  </Text>
                  <HStack flexWrap="wrap" spacing={2}>
                    {result.results.not_found.map((rut, i) => (
                      <Badge key={i} colorScheme="orange" fontFamily="mono" fontSize="xs">{rut}</Badge>
                    ))}
                  </HStack>
                </Box>
              )}

              {/* Errores */}
              {result.results.errors.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2} color="red.400">
                    <Icon as={FiX} mr={1} />Errores ({result.results.errors.length})
                  </Text>
                  {result.results.errors.map((e, i) => (
                    <Text key={i} fontSize="xs" color="red.400">{e.rut}: {e.error}</Text>
                  ))}
                </Box>
              )}

              <HStack justify="flex-end">
                <Button size="sm" colorScheme="purple" variant="outline" onClick={reset}>
                  Importar otro archivo
                </Button>
              </HStack>
            </VStack>
          )}

        </VStack>
      </CardBody>
    </Card>
  );
};

export default BecadosImporter;
